import React, { useState, useEffect } from 'react';
import './App.css';

import { collection, addDoc, getDocs, onSnapshot, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "./firebase"; // your firebase configuration file

// Import child pages
import HomePage from './components/HomePage';
import BlogPage from './components/BlogPage';
import PortfolioPage from './components/PortfolioPage';
import NewPostPage from './components/NewPostPage';
import NewPortfolioPage from './components/NewPortfolioPage';
import BlogDetailsPage from './components/BlogDetailsPage';
// Import the new Navbar component
import Navbar from './components/Navbar';
import CVPage from './components/CVPage';

const PortfolioBlog = () => {
  // Admin check: Only enable admin mode if the URL query parameter "admin" matches our secret
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const adminToken = query.get('admin');
    if (adminToken && adminToken === process.env.REACT_APP_ADMIN_SECRET) {
      setIsAdmin(true);
    }
  }, []);

  const [selectedPost, setSelectedPost] = useState(null);

  // State for blog posts (Firestore integration)
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    const postsQuery = collection(db, "blog-posts");
    const unsubscribe = onSnapshot(postsQuery, (querySnapshot) => {
      const postsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPosts(postsData);
    });
    return unsubscribe; // Clean up the listener on unmount
  }, []);

  // State for portfolio items (Firestore integration)
  const [portfolioItems, setPortfolioItems] = useState([]);
  useEffect(() => {
    const portfolioQuery = collection(db, "portfolio-items");
    const unsubscribe = onSnapshot(portfolioQuery, (querySnapshot) => {
      const itemsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPortfolioItems(itemsData);
    });
    return unsubscribe;
  }, []);

  // Page state, form states, etc.
  const [activePage, setActivePage] = useState('home');
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'Personal' });
  const [newPortfolio, setNewPortfolio] = useState({
    title: '',
    description: '',
    imageUrl: '/api/placeholder/600/400',
    tags: '',
    link: '#',
  });
  const [githubUrl, setGithubUrl] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [darkMode, setDarkMode] = useState(false);

  // GitHub fetch logic
  const fetchRepoData = async () => {
    if (!githubUrl) {
      alert('Please enter a GitHub repository URL.');
      return;
    }
    try {
      // Parse owner and repo name from the URL
      const parts = githubUrl.split('/').filter(Boolean);
      const owner = parts[parts.length - 2];
      const repo = parts[parts.length - 1];

      // Fetch repository data
      const repoResponse = await fetch(`https://api.github.com/repos/${owner}/${repo}`, {
        headers: {
          Authorization: `token ${process.env.REACT_APP_GITHUB_TOKEN}`,
        },
      });
      if (!repoResponse.ok) {
        throw new Error('Failed to fetch repository data');
      }
      const repoData = await repoResponse.json();

      // Fetch the repository README
      let readmeText = '';
      const readmeResponse = await fetch(`https://api.github.com/repos/${owner}/${repo}/readme`, {
        headers: {
          Accept: 'application/vnd.github.v3.raw',
          Authorization: `token ${process.env.REACT_APP_GITHUB_TOKEN}`,
        },
      });
      if (readmeResponse.ok) {
        readmeText = await readmeResponse.text();
      }

      // Update newPortfolio state with data from GitHub
      setNewPortfolio({
        title: repoData.name || '',
        description: repoData.description || '',
        readme: readmeText, // New property for the README content
        imageUrl: repoData.owner?.avatar_url || '/api/placeholder/600/400',
        tags: repoData.language || '',
        link: repoData.html_url || githubUrl,
      });
    } catch (error) {
      console.error(error);
      alert('Failed to fetch repository data. Please check the URL and try again.');
    }
  };

  // Handle new blog post using Firestore
  const handlePostSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && editId) {
        // Update an existing blog post document
        const postRef = doc(db, "blog-posts", editId);
        await updateDoc(postRef, {
          title: newPost.title,
          content: newPost.content,
          category: newPost.category,
          edited: new Date().toISOString(),
        });
        setIsEditing(false);
      } else {
        // Create a new blog post document
        const post = {
          title: newPost.title,
          content: newPost.content,
          date: new Date().toISOString(),
          category: newPost.category,
        };
        await addDoc(collection(db, "blog-posts"), post);
      }
      setNewPost({ title: '', content: '', category: 'Personal' });
    } catch (error) {
      console.error("Error saving post: ", error);
      alert("Error saving post. Please try again.");
    }
  };

  // Handle new portfolio item using Firestore
  const handlePortfolioSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && editId) {
        // Update an existing portfolio document
        const itemRef = doc(db, "portfolio-items", editId);
        await updateDoc(itemRef, {
          title: newPortfolio.title,
          description: newPortfolio.description,
          imageUrl: newPortfolio.imageUrl,
          tags: newPortfolio.tags.split(',').map((tag) => tag.trim()),
          link: newPortfolio.link,
        });
        setIsEditing(false);
      } else {
        // Create a new portfolio document
        const item = {
          title: newPortfolio.title,
          description: newPortfolio.description,
          imageUrl: newPortfolio.imageUrl || '/api/placeholder/600/400',
          tags: newPortfolio.tags.split(',').map((tag) => tag.trim()),
          link: newPortfolio.link,
        };
        await addDoc(collection(db, "portfolio-items"), item);
      }
      setNewPortfolio({
        title: '',
        description: '',
        imageUrl: '/api/placeholder/600/400',
        tags: '',
        link: '#',
      });
      setGithubUrl('');
    } catch (error) {
      console.error("Error saving portfolio item: ", error);
      alert("Error saving portfolio item. Please try again.");
    }
  };

  // Edit blog post (using Firestore state)
  const editPost = (id) => {
    const postToEdit = posts.find((post) => post.id === id);
    setNewPost({
      title: postToEdit.title,
      content: postToEdit.content,
      category: postToEdit.category,
    });
    setIsEditing(true);
    setEditId(id);
    setActivePage('new-post');
  };

  // Edit portfolio item (using Firestore state)
  const editPortfolio = (id) => {
    const itemToEdit = portfolioItems.find((item) => item.id === id);
    setNewPortfolio({
      title: itemToEdit.title,
      description: itemToEdit.description,
      imageUrl: itemToEdit.imageUrl,
      tags: itemToEdit.tags.join(', '),
      link: itemToEdit.link,
    });
    setIsEditing(true);
    setEditId(id);
    setActivePage('new-portfolio');
  };

  // Delete blog post using Firestore
  const deletePost = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deleteDoc(doc(db, "blog-posts", id));
      } catch (error) {
        console.error("Error deleting post: ", error);
        alert("Error deleting post. Please try again.");
      }
    }
  };

  // Delete portfolio item using Firestore
  const deletePortfolio = async (id) => {
    if (window.confirm('Are you sure you want to delete this portfolio item?')) {
      try {
        await deleteDoc(doc(db, "portfolio-items", id));
      } catch (error) {
        console.error("Error deleting portfolio item: ", error);
        alert("Error deleting portfolio item. Please try again.");
      }
    }
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Use the new Navbar component */}
      <Navbar 
        activePage={activePage} 
        setActivePage={setActivePage}
        isAdmin={isAdmin}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 flex-grow">
        {activePage === 'home' && (
          <HomePage
            darkMode={darkMode}
            posts={posts}
            portfolioItems={portfolioItems}
            setActivePage={setActivePage}
            formatDate={formatDate}
            isAdmin={isAdmin}
            setSelectedPost={setSelectedPost}
          />
        )}

        {activePage === 'blog' && (
          <BlogPage
            darkMode={darkMode}
            posts={posts}
            editPost={editPost}
            deletePost={deletePost}
            setActivePage={setActivePage}
            formatDate={formatDate}
            isAdmin={isAdmin}
            setSelectedPost={setSelectedPost}
          />
        )}

        {activePage === 'portfolio' && (
          <PortfolioPage
            darkMode={darkMode}
            portfolioItems={portfolioItems}
            editPortfolio={editPortfolio}
            deletePortfolio={deletePortfolio}
            setActivePage={setActivePage}
            isAdmin={isAdmin}
          />
        )}

        {activePage === 'new-post' && isAdmin && (
          <NewPostPage
            darkMode={darkMode}
            isEditing={isEditing}
            newPost={newPost}
            setNewPost={setNewPost}
            handlePostSubmit={handlePostSubmit}
            setActivePage={setActivePage}
            setIsEditing={setIsEditing}
          />
        )}

        {activePage === 'new-portfolio' && isAdmin && (
          <NewPortfolioPage
            darkMode={darkMode}
            isEditing={isEditing}
            githubUrl={githubUrl}
            setGithubUrl={setGithubUrl}
            fetchRepoData={fetchRepoData}
            newPortfolio={newPortfolio}
            setNewPortfolio={setNewPortfolio}
            handlePortfolioSubmit={handlePortfolioSubmit}
            setActivePage={setActivePage}
            setIsEditing={setIsEditing}
          />
        )}

        {activePage === 'blog-details' && (
          <BlogDetailsPage
            darkMode={darkMode}
            selectedPost={selectedPost}
            setActivePage={setActivePage}
            formatDate={formatDate}
            editPost={editPost}
            deletePost={deletePost}
            isAdmin={isAdmin}
          />
        )}
        {activePage === 'cv' && (
          <CVPage
            darkMode={darkMode}
            setActivePage={setActivePage}
          />
        )}
      </main>

      {/* Footer */}
      <footer className={`py-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className="container mx-auto px-4 text-center">
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            © {new Date().getFullYear()} Berke Aktürk. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PortfolioBlog;